import {
  Button,
  Checkbox,
  Container,
  DateInput,
  Form,
  Input,
  Link,
  List,
  Message,
  RadioGroup,
  Stack,
  Text,
} from "basis";
import { CreditCard } from "components/CreditCard/CreditCard";
import { postAdditionalCardHolder } from "library/api/cards";
import { validateEmail } from "library/formValidation";
import { useMutation } from "react-query";
import { authorityOptions } from "./reducer";
import { ActionType, ScreenProps } from "./types";

export const AdditionalCardHolderForm = ({ state, dispatch }: ScreenProps) => {
  const { accountInfo } = state;
  const additionalCardHolderMutation = useMutation(postAdditionalCardHolder, {
    onSuccess: (_, formValues) => {
      dispatch({ type: ActionType.FORM_SUBMITTED, formValues });
    },

    // AF: TODO When zendesk api integration added.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any, { setErrors }: any) => {
      const errorType = error.response?.data?.type ?? null;

      switch (errorType) {
        case "BAD_REQUEST": {
          setErrors({
            newCreditLimit: "Invalid value",
          });
          break;
        }
        default: {
          // TODO : Zendesk api ticket
          // dispatch({
          //   type: ActionType.ADDITIONAL_CARD_HOLDER_FAILED,
          //   errorType,
          // });
        }
      }
    },
  });

  const onSubmit = ({ errors, values, setErrors }) => {
    if (Object.keys(errors).length === 0) {
      additionalCardHolderMutation.mutate({ ...values, setErrors });
    }
  };

  return (
    <Form initialValues={state.formValues} onSubmit={onSubmit}>
      <Stack gap="10">
        <Container padding="4" bg="grey.t05">
          <CreditCard
            cardName={accountInfo.cardName}
            cardEndsWith={accountInfo.cardEndsWith}
          />
        </Container>
        <Stack gap="8" width="100%" width-sm="400">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Primary card holder details
          </Text>
          <Input name="primaryFullName" label="Full name" />
          <DateInput name="primaryDateOfBirth" label="Date of birth" />
        </Stack>
        <Stack gap="8" width="100%" width-sm="400">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Additional card holder details
          </Text>
          <Input name="additionalFullName" label="Full name" />
          <Input
            type="email"
            name="email"
            label="Email"
            validate={validateEmail}
          />
          <DateInput name="additionalDateOfBirth" label="Date of birth" />
        </Stack>

        <Stack gap="4">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Authority to disclose and maintain
          </Text>
          <Text>
            If you would like your additional cardholder to have access to your
            account information and maintain your account, please select one of
            the options below.
          </Text>
          <RadioGroup
            testId="authorityOption"
            name="authorityOption"
            label="Authority options"
            options={authorityOptions}
            columns={1}
          />

          <List type="ordered">
            <List.Item>
              The person nominated on this form as an additional cardholder to
              request access to my personal information (including credit
              eligibility information) concerning my account; and
            </List.Item>
            <List.Item>
              Latitude to act on such request and to disclose to, and if option
              2 is ticked to receive from, the nominated person such information
              related to my account. And if option 2 is ticked:
            </List.Item>
            <List.Item>
              The person nominated on this form as an additional cardholder to
              maintain my account including to:
              <List>
                <List.Item>
                  Make a change of address and phone numbers;
                </List.Item>
                <List.Item>
                  Request the issue of a new card or activate a card on my
                  account;
                </List.Item>
                <List.Item>
                  Request changes to special promotion details, if incorrect;
                  and
                </List.Item>
                <List.Item>
                  Request fee/charge reversal, if the details are incorrect.
                </List.Item>
              </List>
            </List.Item>
          </List>
          <Text>
            This authority remains in force until I cancel it. I understand that
            I may cancel this authority at any time by written notification to
            Latitude with the termination being effective from the date written
            notification is received by Latitude.
          </Text>
        </Stack>
        <Stack gap="4">
          <Text as="h3" textStyle="heading5" color="primary.blue.t100">
            Declaration
          </Text>

          <Text>I acknowledge that:</Text>

          <List type="ordered">
            <List.Item>
              Each use of the additional card will be subject to the credit
              contract for my account;
            </List.Item>
            <List.Item>
              I will be liable for any use of the additional card, and any
              breach of the credit contract by the additional cardholder;
            </List.Item>
            <List.Item>
              The information provided in this application is true and correct
              and that no information which is or could be relevant to Latitude
              granting credit to me has been withheld;
            </List.Item>
          </List>
          <Checkbox
            name="agreeToAdditionalCardHolder"
            label="Agree to additional card holder"
            hideLabel
          >
            I hereby apply to Latitude to add the person named on this form as
            an additional cardholder on my account
          </Checkbox>

          <Checkbox
            name="agreeToPrivacyPolicy"
            label="Agree to privacy policy to add the person named on this form as an additional cardholder on my account"
            hideLabel
          >
            I have read and agree to the{" "}
            <Link href="https://www.latitudefinancial.com.au/privacy/" newTab>
              Privacy Policy
            </Link>
          </Checkbox>
        </Stack>

        {additionalCardHolderMutation.isError && (
          <Message severity="blocking" bg="secondary.pink.t30">
            Sorry, there was an error. Please try again later.
          </Message>
        )}
        <Button
          type="submit"
          width="256"
          loading={additionalCardHolderMutation.isLoading}
        >
          Submit
        </Button>
      </Stack>
    </Form>
  );
};
